/**
 * Widget Registry for KADMIN Dashboard
 *
 * Defines all available widgets with their data mappings.
 * The dataKey values must match what the backend returns.
 *
 * Backend Response Structure by Category:
 *
 * users:
 *   - total, byRole{}, byLocale{}, byDay{}, recentUsers[]
 *
 * samples:
 *   - total, byStatus{}, byDay{}, recentSamples[]
 *
 * system:
 *   - uptime, memory{}, events{}, timestamp
 */

// Widget types available
export const WIDGET_TYPES = {
  STAT_CARD: "stat-card",
  LINE_CHART: "line-chart",
  BAR_CHART: "bar-chart",
  PIE_CHART: "pie-chart",
  AREA_CHART: "area-chart",
  TABLE: "table",
  LEADERBOARD: "leaderboard",
  GAUGE: "gauge",
  SPARKLINE: "sparkline",
  KPI: "kpi",
  HEATMAP: "heatmap",
  DONUT: "donut",
  TEXT: "text",
};

// Default widget sizes (grid units)
export const DEFAULT_SIZES = {
  [WIDGET_TYPES.STAT_CARD]: { w: 3, h: 2, minW: 2, minH: 2 },
  [WIDGET_TYPES.LINE_CHART]: { w: 6, h: 4, minW: 4, minH: 3 },
  [WIDGET_TYPES.BAR_CHART]: { w: 6, h: 4, minW: 4, minH: 3 },
  [WIDGET_TYPES.PIE_CHART]: { w: 4, h: 4, minW: 3, minH: 3 },
  [WIDGET_TYPES.AREA_CHART]: { w: 6, h: 4, minW: 4, minH: 3 },
  [WIDGET_TYPES.TABLE]: { w: 6, h: 5, minW: 4, minH: 3 },
  [WIDGET_TYPES.LEADERBOARD]: { w: 4, h: 5, minW: 3, minH: 4 },
  [WIDGET_TYPES.GAUGE]: { w: 3, h: 3, minW: 2, minH: 2 },
  [WIDGET_TYPES.SPARKLINE]: { w: 3, h: 2, minW: 2, minH: 2 },
  [WIDGET_TYPES.KPI]: { w: 4, h: 3, minW: 3, minH: 2 },
  [WIDGET_TYPES.HEATMAP]: { w: 8, h: 4, minW: 6, minH: 3 },
  [WIDGET_TYPES.DONUT]: { w: 4, h: 4, minW: 3, minH: 3 },
  [WIDGET_TYPES.TEXT]: { w: 6, h: 2, minW: 2, minH: 1 },
};

/**
 * Widget Registry
 *
 * Each widget has:
 * - id: Unique identifier (used in layout)
 * - type: Widget component type (from WIDGET_TYPES)
 * - title: Display title
 * - category: Metric category it belongs to (maps to CASL subject)
 * - dataKey: Path to data in the category response (dot notation supported)
 * - icon: Material icon name
 * - color: Primary color for the widget
 * - description: Tooltip/description text
 * - format: Optional formatter ('number', 'currency', 'percent')
 * - suffix/prefix: Optional text to add to values
 * - transform: Optional transform function name for complex data
 * - supportsFilters: Optional array of filter types the widget supports
 * - columns: For table widgets, column names
 */
export const widgetRegistry = {
  // ===============================
  // USERS CATEGORY
  // ===============================
  "users-total": {
    id: "users-total",
    type: WIDGET_TYPES.STAT_CARD,
    title: "Total Users",
    category: "users",
    dataKey: "total",
    icon: "people",
    color: "#3B82F6",
    description: "Total registered users",
    format: "number",
  },
  "users-active": {
    id: "users-active",
    type: WIDGET_TYPES.STAT_CARD,
    title: "Active Users",
    category: "users",
    dataKey: "active",
    icon: "person",
    color: "#10B981",
    description: "Active (non-suspended) users",
    format: "number",
  },
  "users-suspended": {
    id: "users-suspended",
    type: WIDGET_TYPES.STAT_CARD,
    title: "Suspended Users",
    category: "users",
    dataKey: "suspended",
    icon: "person_off",
    color: "#EF4444",
    description: "Suspended user accounts",
    format: "number",
  },
  "users-admins": {
    id: "users-admins",
    type: WIDGET_TYPES.STAT_CARD,
    title: "Admins",
    category: "users",
    dataKey: "admins",
    icon: "admin_panel_settings",
    color: "#8B5CF6",
    description: "Users with admin role",
    format: "number",
  },
  "users-by-role": {
    id: "users-by-role",
    type: WIDGET_TYPES.PIE_CHART,
    title: "Users by Role",
    category: "users",
    dataKey: "byRole",
    icon: "pie_chart",
    color: "#3B82F6",
    description: "Distribution of users by role",
    transform: "objectToPieData",
  },
  "users-by-locale": {
    id: "users-by-locale",
    type: WIDGET_TYPES.BAR_CHART,
    title: "Users by Locale",
    category: "users",
    dataKey: "byLocale",
    icon: "language",
    color: "#3B82F6",
    description: "User distribution by locale/region",
    transform: "objectToBarData",
  },
  "users-by-day": {
    id: "users-by-day",
    type: WIDGET_TYPES.LINE_CHART,
    title: "User Registrations",
    category: "users",
    dataKey: "byDay",
    icon: "trending_up",
    color: "#3B82F6",
    description: "New user registrations over time",
    transform: "objectToTimeseriesData",
  },
  "users-recent": {
    id: "users-recent",
    type: WIDGET_TYPES.TABLE,
    title: "Recent Users",
    category: "users",
    dataKey: "recentUsers",
    icon: "person_add",
    color: "#3B82F6",
    description: "Most recently registered users",
    columns: ["displayName", "email", "role", "createdAt"],
  },

  // ===============================
  // SAMPLES CATEGORY
  // ===============================
  "samples-total": {
    id: "samples-total",
    type: WIDGET_TYPES.STAT_CARD,
    title: "Total Samples",
    category: "samples",
    dataKey: "total",
    icon: "inventory_2",
    color: "#EC4899",
    description: "Total sample records",
    format: "number",
  },
  "samples-by-status": {
    id: "samples-by-status",
    type: WIDGET_TYPES.PIE_CHART,
    title: "Samples by Status",
    category: "samples",
    dataKey: "byStatus",
    icon: "pie_chart",
    color: "#EC4899",
    description: "Distribution by sample status",
    transform: "objectToPieData",
  },
  "samples-by-day": {
    id: "samples-by-day",
    type: WIDGET_TYPES.AREA_CHART,
    title: "Samples Over Time",
    category: "samples",
    dataKey: "byDay",
    icon: "show_chart",
    color: "#EC4899",
    description: "Sample creation over time",
    transform: "objectToTimeseriesData",
  },
  "samples-recent": {
    id: "samples-recent",
    type: WIDGET_TYPES.TABLE,
    title: "Recent Samples",
    category: "samples",
    dataKey: "recentSamples",
    icon: "new_releases",
    color: "#EC4899",
    description: "Most recently created samples",
    columns: ["name", "status", "createdBy", "createdAt"],
  },

  // ===============================
  // SYSTEM CATEGORY
  // ===============================
  "system-uptime": {
    id: "system-uptime",
    type: WIDGET_TYPES.STAT_CARD,
    title: "Uptime",
    category: "system",
    dataKey: "uptime",
    icon: "timer",
    color: "#14B8A6",
    description: "Server uptime",
    format: "duration",
  },
  "system-memory": {
    id: "system-memory",
    type: WIDGET_TYPES.GAUGE,
    title: "Memory Usage",
    category: "system",
    dataKey: "memoryPercent",
    icon: "memory",
    color: "#F59E0B",
    description: "Server memory utilization",
    format: "percent",
  },
  "system-events": {
    id: "system-events",
    type: WIDGET_TYPES.STAT_CARD,
    title: "Events Processed",
    category: "system",
    dataKey: "events.total",
    icon: "bolt",
    color: "#6366F1",
    description: "Total event bus events",
    format: "number",
  },
  "system-heap": {
    id: "system-heap",
    type: WIDGET_TYPES.STAT_CARD,
    title: "Heap Used",
    category: "system",
    dataKey: "heapUsed",
    icon: "storage",
    color: "#14B8A6",
    description: "V8 heap memory used",
    format: "bytes",
  },
};

/**
 * Get widgets for a specific category
 */
export function getWidgetsByCategory(category) {
  return Object.values(widgetRegistry).filter((w) => w.category === category);
}

/**
 * Get widget by ID
 */
export function getWidgetById(id) {
  return widgetRegistry[id] || null;
}

/**
 * Get all widget IDs
 */
export function getAllWidgetIds() {
  return Object.keys(widgetRegistry);
}

/**
 * Get all widgets as an array
 */
export function getAllWidgets() {
  return Object.values(widgetRegistry);
}

/**
 * Get default dashboard layout
 */
export function getDefaultLayout() {
  const defaultWidgets = [
    // Row 1: Key stats
    "users-total",
    "users-active",
    "users-admins",
    "samples-total",
    // Row 2: Charts
    "users-by-day",
    "users-by-role",
    // Row 3: Tables
    "users-recent",
    "samples-recent",
  ].filter((id) => widgetRegistry[id]);

  let x = 0;
  let y = 0;
  const cols = 12;

  return defaultWidgets.map((widgetId) => {
    const widget = widgetRegistry[widgetId];
    const size = DEFAULT_SIZES[widget?.type] || { w: 3, h: 2, minW: 2, minH: 2 };

    if (x + size.w > cols) {
      x = 0;
      y += size.h;
    }

    const layout = {
      i: widgetId,
      x,
      y,
      w: size.w,
      h: size.h,
      minW: size.minW,
      minH: size.minH,
    };

    x += size.w;
    return layout;
  });
}

/**
 * Data transformation functions
 */
export const dataTransformers = {
  objectToPieData: (data) => {
    if (!data || typeof data !== "object") return [];
    return Object.entries(data).map(([name, value]) => ({
      name,
      value: typeof value === "object" ? value.count || 0 : value,
    }));
  },

  objectToBarData: (data) => {
    if (!data || typeof data !== "object") return [];
    return Object.entries(data).map(([name, value]) => ({
      name,
      value: typeof value === "object" ? value.count || 0 : value,
    }));
  },

  objectKeyCount: (data) => {
    if (!data || typeof data !== "object") return 0;
    return Object.keys(data).length;
  },

  objectToTimeseriesData: (data) => {
    if (!data || typeof data !== "object") return [];
    return Object.entries(data)
      .map(([date, value]) => ({
        date,
        value: typeof value === "number" ? value : 0,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  },

  arrayToHourlyData: (data) => {
    if (!Array.isArray(data)) return [];
    return data.map((value, hour) => ({
      name: `${hour.toString().padStart(2, "0")}:00`,
      value,
    }));
  },
};

/**
 * Value formatters
 */
export const formatters = {
  number: (value) => {
    if (value === null || value === undefined) return "—";
    return new Intl.NumberFormat().format(value);
  },
  currency: (value) => {
    if (value === null || value === undefined) return "—";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(value);
  },
  percent: (value) => {
    if (value === null || value === undefined) return "—";
    return `${value}%`;
  },
};

/**
 * Category metadata (for UI organization)
 */
export const categoryMeta = {
  users: {
    label: "Users",
    icon: "people",
    color: "#3B82F6",
    description: "User registrations and activity metrics",
  },
  samples: {
    label: "Samples",
    icon: "inventory_2",
    color: "#EC4899",
    description: "Sample CRUD data",
  },
  system: {
    label: "System",
    icon: "settings",
    color: "#14B8A6",
    description: "Server health and performance",
  },
};

// ===============================
// DYNAMIC REGISTRY (Zabbix-like)
// ===============================

/**
 * Runtime registry that starts with static defaults and can be
 * extended with backend-discovered metrics.
 */
let dynamicRegistry = { ...widgetRegistry };
let dynamicCategories = { ...categoryMeta };

/**
 * Merge backend metric definitions into the registry.
 * Backend metrics that don't already exist as static widgets get a
 * default widget type inferred from their `supportedWidgets[0]`.
 *
 * @param {Array} backendMetrics - Array from /api/metrics/registry
 * @param {Array} backendCategories - Array of categories from backend
 * @returns {{ widgets: Object, categories: Object }}
 */
export function mergeBackendRegistry(backendMetrics, backendCategories) {
  const merged = { ...widgetRegistry };

  if (Array.isArray(backendMetrics)) {
    for (const m of backendMetrics) {
      if (merged[m.id]) {
        // Existing static widget — update with any extra backend info
        merged[m.id] = { ...merged[m.id], supportedWidgets: m.supportedWidgets || [], valueType: m.valueType };
      } else {
        // New dynamic metric — create a widget definition
        const widgetType = mapValueTypeToWidget(m.valueType, m.supportedWidgets);
        merged[m.id] = {
          id: m.id,
          type: widgetType,
          title: m.title,
          titleKey: m.titleKey,
          category: m.category,
          dataKey: m.dataKey,
          icon: m.icon || "analytics",
          color: m.color || "#6366F1",
          description: m.description || "",
          format: m.valueType === "number" ? "number" : m.valueType,
          supportedWidgets: m.supportedWidgets || [widgetType],
          valueType: m.valueType,
          columns: m.columns,
          dynamic: true, // Flag to identify backend-discovered metrics
        };
      }
    }
  }

  // Merge categories
  const mergedCats = { ...categoryMeta };
  if (Array.isArray(backendCategories)) {
    for (const cat of backendCategories) {
      if (!mergedCats[cat.category]) {
        mergedCats[cat.category] = {
          label: cat.label,
          labelKey: cat.labelKey,
          icon: getCollectionIcon(cat.category),
          color: getCollectionColor(cat.category),
          description: "",
          metricCount: cat.metricCount,
        };
      } else {
        mergedCats[cat.category].metricCount = cat.metricCount;
      }
    }
  }

  dynamicRegistry = merged;
  dynamicCategories = mergedCats;

  return { widgets: merged, categories: mergedCats };
}

/** Map valueType to a default widget type */
function mapValueTypeToWidget(valueType, supportedWidgets) {
  if (supportedWidgets && supportedWidgets.length > 0) {
    return supportedWidgets[0];
  }
  const map = {
    number: WIDGET_TYPES.STAT_CARD,
    percent: WIDGET_TYPES.GAUGE,
    duration: WIDGET_TYPES.STAT_CARD,
    bytes: WIDGET_TYPES.STAT_CARD,
    timeseries: WIDGET_TYPES.LINE_CHART,
    object: WIDGET_TYPES.PIE_CHART,
    table: WIDGET_TYPES.TABLE,
  };
  return map[valueType] || WIDGET_TYPES.STAT_CARD;
}

function getCollectionIcon(category) {
  const icons = { users: "people", samples: "inventory_2", roles: "admin_panel_settings", notifications: "notifications", invitecodes: "mail", system: "settings" };
  return icons[category] || "folder";
}

function getCollectionColor(category) {
  const colors = { users: "#3B82F6", samples: "#EC4899", roles: "#8B5CF6", notifications: "#F59E0B", invitecodes: "#14B8A6", system: "#14B8A6" };
  return colors[category] || "#6366F1";
}

/** Get the current (potentially enriched) registry */
export function getDynamicRegistry() {
  return dynamicRegistry;
}

/** Get the current category metadata */
export function getDynamicCategories() {
  return dynamicCategories;
}

/** Get widget by ID from dynamic registry */
export function getDynamicWidgetById(id) {
  return dynamicRegistry[id] || null;
}

// Compatibility exports
export const WIDGET_REGISTRY = widgetRegistry;
export const DEFAULT_LAYOUT = getDefaultLayout();

export default widgetRegistry;
